import React, { useCallback, useMemo, useRef } from "react";
import { Sprite, Graphics, Container, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";

/**
 * ButtonHoldAndWinPlay — the play button during Cleopatra's Respins.
 *
 * A copy of FreeSpinButtonPlay (Griffin's is gone). IT NEVER STARTS A
 * RESPIN: the round runs itself (HoldAndWinControlContext). A press lands the
 * cells still turning, all at once, or resumes a reloaded round. Spacebar is
 * owned by the context, so button and key can never disagree.
 *
 * The respins left are on its face: −1 as a respin starts, the server's value
 * once it lands.
 */

// Position and size — IDENTICAL to secondary ButtonPlay, so the button does
// not move when the toolbar swaps one for the other mid-round.
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.45 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.175 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.375 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 2.25 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.675 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.0 },
  },
};

const ButtonHoldAndWinPlay = ({ toolbar }) => {
  const { t } = useTranslation();
  const control = useHoldAndWinControl();
  const spinning = !!control?.spinning;
  const awaitingResume = !!control?.awaitingResume;
  const respins = control?.displayRespins ?? 0;

  const {
    dimensions,
    uiSymbolSize,
    device,
    orientation,
    getResponsiveConfig,
    isMobileSmall,
  } = useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // The SHARED play-button sprite — the context rotates and pulses it.
  const { playButtonRef: spriteRef } = useReelRefs();

  const brightnessFilter = useRef(null);
  if (!brightnessFilter.current) {
    brightnessFilter.current = new PIXI.ColorMatrixFilter();
    brightnessFilter.current.brightness(1.6, false);
    brightnessFilter.current.resolution = window.devicePixelRatio || 1;
    brightnessFilter.current.padding = 8;
  }

  const handlePointerOver = useCallback(() => {
    const sprite = spriteRef.current;
    if (sprite && (!sprite.filters || sprite.filters.length === 0)) {
      sprite.filters = [brightnessFilter.current];
    }
  }, [spriteRef]);

  const handlePointerOut = useCallback(() => {
    if (spriteRef.current) spriteRef.current.filters = [];
  }, [spriteRef]);

  // Live only when a press has an action behind it.
  const isInteractive = spinning || awaitingResume;

  const handleTap = useCallback(() => {
    control?.handlePress?.();
  }, [control]);

  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.075,
        };
      }
      return {
        x: toolbar.width - buttonConfig.width * 0.55,
        y: -dimensions.height / 2 + buttonConfig.height / 1.8,
      };
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y:
            -toolbar.visualHeight -
            buttonConfig.height / 2 -
            buttonConfig.height * 0.065,
        };
      }
      return {
        x: toolbar.width - buttonConfig.width * 0.415,
        y: -dimensions.height / 2 + buttonConfig.height / 1.8,
      };
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return { x: toolbar.width / 2, y: -dimensions.height / 5.0 };
      }
      return {
        x: toolbar.width - buttonConfig.width / 1.4,
        y: toolbar.height - buttonConfig.height / 1.7,
      };
    }
    return {
      x: toolbar.width - buttonConfig.width * 1.285,
      y: uiSymbolSize * 0.08,
    };
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
    uiSymbolSize,
  ]);

  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width],
  );

  const counterTextStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: '"Noto Sans", sans-serif',
        fontSize: Math.round(buttonConfig.width * 0.32),
        fontWeight: "800",
        fill: 0xffffff,
        align: "center",
      }),
    [buttonConfig.width],
  );

  const iconTexture = getNewUITexture("icon_play");

  return (
    <Container
      name="secondary_hold_and_win_button_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      <Graphics
        name="hold_and_win_button_play_interactive_area"
        eventMode={isInteractive ? "static" : "none"}
        cursor={isInteractive ? "pointer" : "default"}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        onpointertap={handleTap}
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
        accessible={true}
        accessibleTitle={
          spinning
            ? t("stop_button", { ns: "common" })
            : t("play_button", { ns: "common" })
        }
      />

      <Sprite
        ref={spriteRef}
        texture={iconTexture}
        name="hold_and_win_button_play_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        roundPixels={true}
        x={0}
        y={0}
        eventMode="none"
        resolution={2}
      />

      {/* Respins left, on the face of the button. */}
      <Text
        text={String(respins)}
        style={counterTextStyle}
        anchor={0.5}
        x={0}
        y={0}
        zIndex={1}
        eventMode="none"
        roundPixels={true}
        resolution={2}
      />
    </Container>
  );
};

export default ButtonHoldAndWinPlay;
